import { useEffect, useState } from 'react';
import { getAdminStores, AdminStore } from 'services/api/admin';
import { useLatest, useRequest } from 'ahooks';
import { useMemo } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import StoreScopesModal from 'components/StoreScopes/StoreScopesModal';
import { StoreIdsPreviewer } from 'views/Aide/components/StoreIdSelect';
import { removeStoreScopesAll } from 'views/BootGuide/views/AiModel/share';
import { Store } from 'components/StoreScopes';

export type StoreIdSelectProps = {
  editable?: boolean;
  value?: General.StoreScope[];
  storeType: Knowledge.StoreType;
  dataSource?: Store[];
  onChange?: (value: General.StoreScope[]) => void;
  onIdsChange?: (value: number[]) => void;
  filter?: (source?: AdminStore[]) => AdminStore[];
};

export const groupBy = (arr: any[] = []) => {
  return arr.reduce((res, item) => {
    const { externalType: platform, region } = item;
    const key = `${platform}-${region}`;
    res[key] = res[key] || [];
    res[key].push(item);
    return res;
  }, {});
};

export const useStoreScope2StoreIds = (
  arr?: General.StoreScope[],
  source?: Record<string, any[]>,
) => {
  return useMemo(() => {
    return (arr || []).reduce<number[]>((res, item) => {
      const { platform, regionStoreScopes } = item;
      for (let i = 0; i < regionStoreScopes.length; i++) {
        const { region, storeIds, storeCount } = regionStoreScopes[i];
        if (region === 'all') {
          res = res.concat(
            Object.entries(source || {})?.reduce((acc2, cur2) => {
              const [key, value] = cur2;
              if (key.startsWith(platform)) {
                acc2 = acc2.concat(value?.map((e) => e.id));
                return acc2;
              }
              return acc2;
            }, []),
          );
          break;
        } else {
          if (storeIds?.length === 0 && storeCount > 0) {
            res = res.concat(source[`${platform}-${region}`]?.map((e) => e.id));
          } else {
            res = res.concat(storeIds);
          }
        }
      }
      return res;
    }, []);
  }, [arr, source]);
};

export const useGetAdminStoresByStoreType = (storeType: Knowledge.StoreType, manual?: boolean) => {
  const { data, loading } = useRequest(
    () =>
      getAdminStores({
        page: 1,
        pageSize: 99999,
        storeType: storeType === 'ALL' ? undefined : storeType,
      }),
    {
      manual,
      refreshDeps: [storeType],
      cacheKey: `wudi-${storeType}`,
    },
  );
  return {
    data: data?.data?.data?.stores,
    loading,
  };
};

function StoreSelect(props: StoreIdSelectProps) {
  const { dataSource, storeType, editable, onChange, value, onIdsChange, filter } = props;
  const onIdsChangeRef = useLatest(onIdsChange);

  const { data: adminStores, loading } = useGetAdminStoresByStoreType(
    storeType,
    Boolean(dataSource),
  );

  const storeDataSource = useMemo(() => {
    return dataSource ? dataSource : filter ? filter(adminStores) : adminStores;
  }, [dataSource, adminStores, filter]);

  const storeMap = useMemo(() => {
    return (storeDataSource || []).reduce<Record<number, Store>>((res, store) => {
      res[store.id] = store;
      return res;
    }, {});
  }, [storeDataSource]);

  const source = useMemo(() => {
    return groupBy(storeDataSource);
  }, [storeDataSource]);

  const ids = useStoreScope2StoreIds(value, source);

  useEffect(() => {
    onIdsChangeRef.current?.(ids);
  }, [ids, onIdsChangeRef]);

  const [visible, setVisible] = useState(false);

  if (loading) {
    return <Spin />;
  }

  const modal = visible && (
    <StoreScopesModal
      visible={visible}
      onCancel={() => setVisible(false)}
      value={value}
      onChange={(value) =>
        onChange?.(storeDataSource ? removeStoreScopesAll(value, storeDataSource) : value)
      }
      showFooterTips={false}
      storeMap={storeMap}
      storeDataSource={storeDataSource || []}
    />
  );

  const preview = (
    <StoreIdsPreviewer value={ids} storeMap={storeMap} storeDataSource={storeDataSource || []} />
  );

  return (
    <>
      {modal}
      {preview}
      {editable && <EditOutlined className="ml-[6px]" onClick={() => setVisible(true)} />}
    </>
  );
}

export default StoreSelect;
